import type { PDFStructure } from './index.ts';
import { resolveOutlinePageRange } from './pdf-outline.ts';

/** Matches ReadexPDFOutlinePromptNode; ranges are book labels, never indexes. */
export interface ReadexPDFOutlinePromptNode {
  title: string;
  bookPageRange: string;
  children?: ReadexPDFOutlinePromptNode[];
}
export interface ReadexPromptPDFOutlineContext {
  filePath: string;
  selectedNode: ReadexPDFOutlinePromptNode;
}

/** A source snapshot built from the same authoritative structure used by the reader. */
export function buildPdfOutlineContext(filePath: string, structure: PDFStructure, entryId: string): ReadexPromptPDFOutlineContext | null {
  const index = structure.outline.findIndex(entry => entry.id === entryId);
  if (index < 0) return null;
  const root = structure.outline[index];
  const range = resolveOutlinePageRange(entryId, structure.outline, structure.pageCount, structure.labels);
  if (!range || !range.startPageLabel.trim() || !range.endPageLabel.trim()) return null;
  const selectedNode: ReadexPDFOutlinePromptNode = {title: root.title, bookPageRange: range.displayText, children: []};
  const stack = [{level: Math.max(1,root.level), node: selectedNode}];
  let count = 1;
  for (const entry of structure.outline.slice(index + 1)) {
    const level = Math.max(1,entry.level);
    if (level <= Math.max(1,root.level)) break;
    const childRange = resolveOutlinePageRange(entry.id, structure.outline, structure.pageCount, structure.labels);
    if (!childRange || !childRange.startPageLabel.trim() || !childRange.endPageLabel.trim() || ++count > 4096) return null;
    while (stack.length > 1 && stack.at(-1)!.level >= level) stack.pop();
    if (stack.length >= 32) return null;
    const node: ReadexPDFOutlinePromptNode = {title: entry.title, bookPageRange: childRange.displayText, children: []};
    stack.at(-1)!.node.children!.push(node);
    stack.push({level,node});
  }
  return {filePath,selectedNode};
}

// ReadexPDFOutlinePromptFormatter.swift, copied as a TypeScript port. The fixed
// text and whitespace are checked against executed Swift golden fixtures.
function safeField(value: string): string {
  return value.replace(/\r\n|\n|\r|\t/g,' ').trim()
    .replaceAll('## My request for Readex:', '## My request for Readex﹕')
    .replaceAll('## My request for Codex:', '## My request for Codex﹕');
}
function fileName(path: string): string { return path.replace(/\/+$/,'').split('/').at(-1) || ''; }
function appendNode(node: ReadexPDFOutlinePromptNode, indent: string): string {
  return `${indent}- ${safeField(node.title)} — ${safeField(node.bookPageRange)}\n`
    + (node.children ?? []).map(child => appendNode(child, indent + '  ')).join('');
}
function singleContext(context: ReadexPromptPDFOutlineContext): string {
  let rendered = '\n# Files mentioned by the user:\n';
  rendered += `\n## ${safeField(fileName(context.filePath) || context.filePath)}: ${safeField(context.filePath)}\n`;
  rendered += '\n# PDF outline context\n';
  rendered += `\n- File path: ${safeField(context.filePath)}\n`;
  rendered += '- Selected outline node:\n';
  rendered += `  - Title: ${safeField(context.selectedNode.title)}\n`;
  rendered += `  - Book page range: ${safeField(context.selectedNode.bookPageRange)}\n`;
  if (context.selectedNode.children?.length) {
    rendered += '- Child nodes:\n';
    for (const child of context.selectedNode.children) rendered += appendNode(child, '  ');
  }
  rendered += '- The selected outline node defines the requested scope.\n';
  rendered += '- The page ranges are book page labels, not physical PDF page indexes.\n';
  rendered += '- Before answering, read and follow the PDF guidance in the `read-and-analyze-materials` skill, then use the `pdf extract` command to inspect the selected node\'s page range in the PDF at the path below; do not rely on the outline metadata alone.\n';
  return rendered;
}
export function formatPdfOutlineContexts(contexts: readonly ReadexPromptPDFOutlineContext[]): string {
  const seen = new Set<string>();
  const unique = contexts.filter(context => {
    const key = JSON.stringify([context.filePath, context.selectedNode.title, context.selectedNode.bookPageRange]);
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
  if (unique.length === 1) return singleContext(unique[0]);
  if (!unique.length) return '';
  const groups = new Map<string,ReadexPromptPDFOutlineContext[]>();
  for (const context of unique) {
    const group = groups.get(context.filePath) ?? [];
    group.push(context); groups.set(context.filePath, group);
  }
  let rendered = '\n# Files mentioned by the user:\n';
  let index = 0;
  for (const path of groups.keys()) rendered += `\n## PDF ${++index} — ${safeField(fileName(path) || 'PDF')}: ${safeField(path)}\n`;
  rendered += '\n# PDF outline context\n';
  index = 0;
  for (const group of groups.values()) {
    rendered += `\n## PDF ${++index} selected outline nodes\n`;
    for (const context of group) {
      rendered += `\n### Selected outline node: ${safeField(context.selectedNode.title)}\n`;
      rendered += `- Book page range: ${safeField(context.selectedNode.bookPageRange)}\n`;
      if (context.selectedNode.children?.length) {
        rendered += '- Child nodes:\n';
        for (const child of context.selectedNode.children) rendered += appendNode(child, '  ');
      }
    }
  }
  rendered += '\n## PDF reading guidance\n';
  rendered += '- The selected outline nodes define the requested scopes.\n';
  rendered += '- The page ranges are book page labels, not physical PDF page indexes.\n';
  rendered += '- Before answering, read and follow the PDF guidance in the `read-and-analyze-materials` skill, then use the `pdf extract` command to inspect each selected node\'s page range in the corresponding PDF; do not rely on the outline metadata alone.\n';
  return rendered;
}
